/**
 * Explorer view switcher. Auto per-form tables grouped by form group (mirrors the
 * column-picker / form-builder hierarchy), then saved private + shared views.
 */
import { useState } from 'react'
import {
  ChevronDown, ChevronLeft, Table2, Layers, Star, Lock, Users2, MoreHorizontal, Trash2, Sparkles,
} from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { ExplorerGroup } from '../api/explorer-api'
import type { AiSavedView } from '../api/ai-storage'
import type { DataViewRow } from '../types/data-view-spec'

export type ActiveSelection =
  | { type: 'form'; formId: string }
  | { type: 'view'; viewId: string }
  // a saved AI "custom table" — rendered through the unified explorer surface
  | { type: 'ai'; viewId: string }

interface ViewSidebarProps {
  groups: ExplorerGroup[]
  views: DataViewRow[]
  aiViews: AiSavedView[]
  currentUserId: string | undefined
  active: ActiveSelection | null
  onSelectForm: (formId: string) => void
  onSelectView: (view: DataViewRow) => void
  onDeleteView: (view: DataViewRow) => void
  onSelectAiView: (view: AiSavedView) => void
  onDeleteAiView: (view: AiSavedView) => void
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-3 pb-1 text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-wide">
      {children}
    </div>
  )
}

export function ViewSidebar({
  groups, views, aiViews, currentUserId, active,
  onSelectForm, onSelectView, onDeleteView, onSelectAiView, onDeleteAiView,
}: ViewSidebarProps) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const toggle = (id: string) => setCollapsed((c) => ({ ...c, [id]: !c[id] }))

  const myViews = views.filter((v) => v.owner_id === currentUserId && !v.is_default)
  const sharedViews = views.filter((v) => v.visibility === 'project')
  const defaults = views.filter((v) => v.is_default)

  return (
    <aside className="w-full h-full overflow-y-auto flex flex-col">
      <SectionLabel>فرم‌ها</SectionLabel>
      <div className="px-1.5">
        {groups.map((g) => {
          const isCol = collapsed[g.id]
          return (
            <div key={g.id} className="mb-0.5">
              <button
                onClick={() => toggle(g.id)}
                className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm text-foreground hover:bg-muted transition-colors"
              >
                {isCol ? <ChevronLeft className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
                <Layers className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="flex-1 text-start truncate font-medium">{g.title}</span>
              </button>
              {!isCol && (
                <div className="ms-3 ps-2 border-s border-border">
                  {g.forms.map((f) => {
                    const isActive = active?.type === 'form' && active.formId === f.id
                    return (
                      <button
                        key={f.id}
                        onClick={() => onSelectForm(f.id)}
                        className={cn(
                          'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors',
                          isActive ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                        )}
                      >
                        <Table2 className={cn('w-3.5 h-3.5 shrink-0', isActive && 'text-primary')} />
                        <span className="flex-1 text-start truncate">{f.title}</span>
                        {f.instanceType === 'repeatable' && (
                          <span className="text-[10px] text-muted-foreground/60">چندتایی</span>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
        {groups.length === 0 && (
          <p className="px-3 py-2 text-xs text-muted-foreground">فرم منتشرشده‌ای وجود ندارد.</p>
        )}
      </div>

      <SavedSection label="نماهای پیش‌فرض" icon={Star} views={defaults} active={active} currentUserId={currentUserId} onSelectView={onSelectView} onDeleteView={onDeleteView} />
      <SavedSection label="نماهای من" icon={Lock} views={myViews} active={active} currentUserId={currentUserId} onSelectView={onSelectView} onDeleteView={onDeleteView} />
      <SavedSection label="نماهای اشتراکی" icon={Users2} views={sharedViews.filter((v) => !v.is_default)} active={active} currentUserId={currentUserId} onSelectView={onSelectView} onDeleteView={onDeleteView} />
      <AiSavedSection views={aiViews} active={active} currentUserId={currentUserId} onSelectView={onSelectAiView} onDeleteView={onDeleteAiView} />
    </aside>
  )
}

/** AI-generated "custom tables" — same look & behavior as a saved-view section. */
function AiSavedSection({
  views, active, currentUserId, onSelectView, onDeleteView,
}: {
  views: AiSavedView[]
  active: ActiveSelection | null
  currentUserId: string | undefined
  onSelectView: (v: AiSavedView) => void
  onDeleteView: (v: AiSavedView) => void
}) {
  if (views.length === 0) return null
  return (
    <>
      <SectionLabel><span className="inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" />جدول‌های هوش مصنوعی</span></SectionLabel>
      <div className="px-1.5">
        {views.map((v) => {
          const isActive = active?.type === 'ai' && active.viewId === v.id
          const canDelete = v.owner_id === currentUserId
          return (
            <div
              key={v.id}
              className={cn(
                'group flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors',
                isActive ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted',
              )}
            >
              <button onClick={() => onSelectView(v)} className="flex-1 flex items-center gap-2 text-start min-w-0">
                <Table2 className={cn('w-3.5 h-3.5 shrink-0', isActive && 'text-primary')} />
                <span className="flex-1 truncate">{v.title}</span>
              </button>
              {canDelete && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={() => onDeleteView(v)} className="text-destructive focus:text-destructive">
                      <Trash2 className="w-4 h-4 me-2" />حذف جدول
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )
        })}
      </div>
    </>
  )
}

function SavedSection({
  label, icon: Icon, views, active, currentUserId, onSelectView, onDeleteView,
}: {
  label: string
  icon: typeof Star
  views: DataViewRow[]
  active: ActiveSelection | null
  currentUserId: string | undefined
  onSelectView: (v: DataViewRow) => void
  onDeleteView: (v: DataViewRow) => void
}) {
  if (views.length === 0) return null
  return (
    <>
      <SectionLabel><span className="inline-flex items-center gap-1.5"><Icon className="w-3 h-3" />{label}</span></SectionLabel>
      <div className="px-1.5">
        {views.map((v) => {
          const isActive = active?.type === 'view' && active.viewId === v.id
          const canDelete = v.owner_id === currentUserId
          return (
            <div
              key={v.id}
              className={cn(
                'group flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors',
                isActive ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted',
              )}
            >
              <button onClick={() => onSelectView(v)} className="flex-1 flex items-center gap-2 text-start min-w-0">
                <Table2 className={cn('w-3.5 h-3.5 shrink-0', isActive && 'text-primary')} />
                <span className="flex-1 truncate">{v.name}</span>
              </button>
              {canDelete && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={() => onDeleteView(v)} className="text-destructive focus:text-destructive">
                      <Trash2 className="w-4 h-4 me-2" />حذف نما
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )
        })}
      </div>
    </>
  )
}
